import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTenantApiToken } from '../../contexts/TenantProfileContext';
import {
  cachedProspectDocument,
  loadProspectDocument,
  type CachedProspectDocument,
} from '../../utils/application-document-cache';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const SHOW_DELAY_MS = 180;
const GAP = 12;
const PAD = 16;
const TITLE_BAR = 41;

function placePreview(anchor: DOMRect): { top: number; left: number; width: number; bodyHeight: number } {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const spaceBelow = Math.max(0, viewportHeight - anchor.bottom - GAP - PAD);
  const spaceAbove = Math.max(0, anchor.top - GAP - PAD);
  const below = spaceBelow >= spaceAbove;
  const room = Math.min(below ? spaceBelow : spaceAbove, viewportHeight - PAD * 2);
  const width = Math.min(920, viewportWidth - PAD * 2, Math.max(420, Math.floor(viewportWidth * 0.72)));
  const bodyHeight = Math.max(160, Math.floor(room - TITLE_BAR));
  const cardHeight = bodyHeight + TITLE_BAR;
  const top = below
    ? Math.min(anchor.bottom + GAP, Math.max(PAD, viewportHeight - PAD - cardHeight))
    : Math.max(PAD, anchor.top - GAP - cardHeight);
  const preferredLeft = anchor.left + anchor.width / 2 - width / 2;
  const left = Math.min(Math.max(PAD, preferredLeft), Math.max(PAD, viewportWidth - PAD - width));
  return { top, left, width, bodyHeight };
}

type PreviewKind = 'image' | 'pdf' | 'none';

function previewKind(blob: Blob, title: string): PreviewKind {
  const type = blob.type.toLowerCase();
  if (type === 'application/pdf' || title.toLowerCase().endsWith('.pdf')) return 'pdf';
  if (type.startsWith('image/') && !type.includes('heic') && !type.includes('heif') && !type.includes('tiff')) {
    return 'image';
  }
  const extension = title.toLowerCase().split('.').pop() ?? '';
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'].includes(extension)) return 'image';
  return 'none';
}

function PreviewBody({
  file,
  title,
  width,
  bodyHeight,
}: {
  file: CachedProspectDocument | null;
  title: string;
  width: number;
  bodyHeight: number;
}) {
  if (!file) return null;
  const kind = previewKind(file.blob, title);
  if (kind === 'image') {
    return (
      <img
        src={file.objectUrl}
        alt=""
        className="w-full bg-gray-50 dark:bg-white/5 object-contain"
        style={{ height: bodyHeight }}
      />
    );
  }
  if (kind === 'pdf') {
    return (
      <div className="overflow-auto bg-gray-50 dark:bg-white/5" style={{ height: bodyHeight }}>
        <Document file={file.objectUrl} loading={<p className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading preview…</p>}>
          <Page pageNumber={1} width={width - 2} renderTextLayer={false} renderAnnotationLayer={false} />
        </Document>
      </div>
    );
  }
  return <p className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">This file can&apos;t be previewed here.</p>;
}

/** The uploaded file itself, sized to the space it sits in. */
export function DocumentInlinePreview({ documentId, title }: { documentId: string; title: string }): ReactNode {
  const getToken = useTenantApiToken();
  const frameRef = useRef<HTMLDivElement>(null);
  const [frameWidth, setFrameWidth] = useState(160);
  const [file, setFile] = useState<CachedProspectDocument | null>(() => cachedProspectDocument(documentId));
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(file ? 'ready' : 'loading');

  useEffect(() => {
    const hit = cachedProspectDocument(documentId);
    if (hit) {
      setFile(hit);
      setStatus('ready');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    void loadProspectDocument(getToken, documentId)
      .then((next) => {
        if (cancelled) return;
        setFile(next);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [documentId, getToken]);

  useLayoutEffect(() => {
    const node = frameRef.current;
    if (!node) return;
    const measure = () => setFrameWidth(Math.max(80, Math.floor(node.clientWidth)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const kind = file ? previewKind(file.blob, title) : 'none';

  return (
    <div ref={frameRef} className="relative h-full min-h-52 overflow-hidden rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5">
      {status === 'loading' ? <p className="absolute inset-0 flex items-center justify-center px-3 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</p> : null}
      {status === 'error' ? <p className="absolute inset-0 flex items-center justify-center px-3 text-center text-sm text-gray-500 dark:text-gray-400">Preview isn&apos;t available.</p> : null}
      {status === 'ready' && kind === 'image' && file ? (
        <img src={file.objectUrl} alt="" className="absolute inset-0 h-full w-full object-contain" />
      ) : null}
      {status === 'ready' && kind === 'pdf' && file ? (
        <div className="absolute inset-0 overflow-auto">
          <Document file={file.objectUrl} loading={<p className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</p>}>
            <Page pageNumber={1} width={frameWidth} renderTextLayer={false} renderAnnotationLayer={false} />
          </Document>
        </div>
      ) : null}
      {status === 'ready' && kind === 'none' ? <p className="absolute inset-0 flex items-center justify-center px-3 text-center text-sm text-gray-500 dark:text-gray-400">This file can&apos;t be previewed here.</p> : null}
    </div>
  );
}

function PreviewCard({
  documentId,
  title,
  anchor,
}: {
  documentId: string;
  title: string;
  anchor: DOMRect;
}) {
  const getToken = useTenantApiToken();
  const [file, setFile] = useState<CachedProspectDocument | null>(() => cachedProspectDocument(documentId));
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(file ? 'ready' : 'loading');
  const frame = placePreview(anchor);

  useEffect(() => {
    const hit = cachedProspectDocument(documentId);
    if (hit) {
      setFile(hit);
      setStatus('ready');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    void loadProspectDocument(getToken, documentId)
      .then((next) => {
        if (cancelled) return;
        setFile(next);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [documentId, getToken]);

  return createPortal(
    <div
      className="pointer-events-none fixed z-[80] overflow-hidden rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 shadow-bento"
      style={{ top: frame.top, left: frame.left, width: frame.width }}
      role="tooltip"
    >
      <p className="truncate border-b border-gray-100 dark:border-white/5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">{title}</p>
      {status === 'loading' ? <p className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400" style={{ height: frame.bodyHeight }}>Loading preview…</p> : null}
      {status === 'error' ? <p className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Preview isn&apos;t available.</p> : null}
      {status === 'ready' ? (
        <PreviewBody file={file} title={title} width={frame.width} bodyHeight={frame.bodyHeight} />
      ) : null}
    </div>,
    document.body,
  );
}

interface DocumentHoverPreviewProps {
  documentId: string;
  title: string;
  className?: string;
  onOpen: () => void;
}

/** Title of an uploaded file. Hover or keyboard focus opens a cached preview. */
export default function DocumentHoverPreview({ documentId, title, className = '', onOpen }: DocumentHoverPreviewProps): ReactNode {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hovering = useRef(false);
  const focused = useRef(false);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);

  const clearShowTimer = useCallback(() => {
    if (showTimer.current) {
      clearTimeout(showTimer.current);
      showTimer.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    clearShowTimer();
    setOpen(false);
  }, [clearShowTimer]);

  const scheduleShow = useCallback(() => {
    clearShowTimer();
    showTimer.current = setTimeout(() => {
      showTimer.current = null;
      if (!hovering.current && !focused.current) return;
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setAnchor(rect);
      setOpen(true);
    }, SHOW_DELAY_MS);
  }, [clearShowTimer]);

  useEffect(() => () => clearShowTimer(), [clearShowTimer]);

  useEffect(() => {
    if (!open) return;
    const update = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) setAnchor(rect);
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`block text-left text-sm text-gray-900 underline dark:text-gray-100 ${className}`.trim()}
        onClick={onOpen}
        onMouseEnter={() => {
          hovering.current = true;
          scheduleShow();
        }}
        onMouseLeave={() => {
          hovering.current = false;
          if (!focused.current) hide();
        }}
        onFocus={() => {
          focused.current = true;
          scheduleShow();
        }}
        onBlur={() => {
          focused.current = false;
          if (!hovering.current) hide();
        }}
      >
        {title}
      </button>
      {open && anchor ? <PreviewCard documentId={documentId} title={title} anchor={anchor} /> : null}
    </>
  );
}
